/**
 * Structured JSON logger — the TS analog of `configure_logging` /
 * `structlog.get_logger`. Emits one JSON line per event with the canonical base
 * envelope, merges the bound request-scoped context, and runs the redactor as
 * the final step before serialization.
 *
 * Isomorphic: no Node/DOM imports. The Node stdout default is reached via
 * `globalThis.process` (undefined in the browser), so the browser tier can use
 * the same `createLogger` with a console sink and an explicit context.
 */

import { redactEntry } from "./redact";
import type { LogLevel } from "./types";

export type LogFields = Record<string, unknown>;
export type LogSink = (line: string) => void;

export interface LoggerOptions {
  /** Datadog `service` value, e.g. "platform-bff". */
  service: string;
  /** Default `logger` name stamped on lines. Defaults to `service`. */
  logger?: string;
  /** Where a serialized line goes. Defaults to Node stdout, else `console.log`. */
  sink?: LogSink;
  /** Returns the bound request-scoped context merged into every line. Defaults to none. */
  getBoundContext?: () => LogFields;
  /** ISO-8601 timestamp source; injectable for tests. Defaults to `new Date().toISOString()`. */
  clock?: () => string;
  /** Run the redactor over every line before serialization. Default `true`. */
  redact?: boolean;
  /** Master switch: when `false`, every call is a no-op (nothing emitted). Default `true`. */
  enabled?: boolean;
  /** Minimum level to emit. Default `"debug"` (emit all). */
  minLevel?: LogLevel;
  /**
   * Key to nest the bound request-scoped context under, matching the Python
   * `configure_logging` `context_key` (default `"context"` → e.g.
   * `{..., "context": {"correlation_id": …, "session_id": …}}`). Per-call fields
   * stay top-level. `null` emits the bound context flat (legacy).
   */
  contextKey?: string | null;
}

export interface Logger {
  debug(event: string, fields?: LogFields): void;
  info(event: string, fields?: LogFields): void;
  warning(event: string, fields?: LogFields): void;
  error(event: string, fields?: LogFields): void;
  critical(event: string, fields?: LogFields): void;
  /** A logger that stamps a different `logger` name (same service/sink/context). */
  named(logger: string): Logger;
  /**
   * A logger with extra bound context merged in (structlog-style `bind`), nested
   * under `contextKey` alongside `getBoundContext()`. The merged values are
   * captured now, so a value logged later (e.g. an async body read) still carries
   * the value from when `with` was called, not a since-mutated one.
   */
  with(context: LogFields): Logger;
}

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warning: 30,
  error: 40,
  critical: 50,
};

const defaultSink: LogSink = (line) => {
  const proc = (globalThis as { process?: { stdout?: { write?: (chunk: string) => void } } }).process;
  if (proc?.stdout?.write) {
    proc.stdout.write(line + "\n");
  } else {
    console.log(line);
  }
};

const defaultClock = (): string => new Date().toISOString();

/** Build a logger that emits the canonical structured-log shape. */
export function createLogger(options: LoggerOptions): Logger {
  const {
    service,
    sink = defaultSink,
    getBoundContext = () => ({}),
    clock = defaultClock,
    redact = true,
    enabled = true,
    minLevel = "debug",
    contextKey = "context",
  } = options;
  const threshold = LEVEL_RANK[minLevel];

  const make = (loggerName: string, extraContext: LogFields = {}): Logger => {
    const emit = (level: LogLevel, event: string, fields?: LogFields): void => {
      if (!enabled) return;
      if (LEVEL_RANK[level] < threshold) return;
      const entry: Record<string, unknown> = {
        timestamp: clock(),
        level,
        logger: loggerName,
        service,
        event,
      };
      // Bound request-scoped context (ambient `getBoundContext()` + any per-call
      // context from `with`) nests under `contextKey` (default "context"),
      // mirroring the Python `_nest_contextvars` processor so one Datadog query
      // works across services; `null` emits it flat (legacy).
      const bound = { ...getBoundContext(), ...extraContext };
      if (contextKey === null) {
        Object.assign(entry, bound);
      } else if (Object.keys(bound).length > 0) {
        entry[contextKey] = bound;
      }
      if (fields) Object.assign(entry, fields);
      sink(JSON.stringify(redact ? redactEntry(entry) : entry));
    };
    return {
      debug: (event, fields) => emit("debug", event, fields),
      info: (event, fields) => emit("info", event, fields),
      warning: (event, fields) => emit("warning", event, fields),
      error: (event, fields) => emit("error", event, fields),
      critical: (event, fields) => emit("critical", event, fields),
      named: (name) => make(name, extraContext),
      with: (context) => make(loggerName, { ...extraContext, ...context }),
    };
  };

  return make(options.logger ?? service);
}
